import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, fmtDate, fmtMoney, fmtNum } from "@/lib/api";
import { StatusBadge } from "./StatusBadge";

export function MotifDialog({ open, onOpenChange, title, description, confirmLabel = "Confirmer", onConfirm, required = true }) {
  const [motif, setMotif] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setMotif("");
  }, [open]);
  const go = async () => {
    setBusy(true);
    try {
      await onConfirm(motif);
      onOpenChange(false);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="motif-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <Textarea placeholder={required ? "Motif (obligatoire)" : "Commentaire (facultatif)"} value={motif}
          onChange={(e) => setMotif(e.target.value)} data-testid="motif-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Retour</Button>
          <Button onClick={go} disabled={busy || (required && motif.trim().length < 3)} data-testid="motif-confirm-button">
            {busy ? "Traitement…" : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const ACTION_LABELS = {
  creation: "Création", modification: "Modification", suppression: "Suppression", validation: "Validation",
  annulation: "Annulation", affectation: "Affectation", reaffectation: "Réaffectation", desaffectation: "Désaffectation",
  desactivation: "Désactivation", reactivation: "Réactivation", reglement: "Règlement", connexion: "Connexion",
  rattachement: "Rattachement", changement_superviseur: "Changement de superviseur", changement_statut: "Changement de statut",
  ajout_piece: "Ajout de pièce jointe", suppression_piece: "Suppression de pièce jointe",
  approbation: "Approbation", rejet: "Rejet", annulation_paiement: "Annulation de paiement",
  stock_negatif: "Autorisation stock négatif", comptage_inventaire: "Comptage d'inventaire",
  import: "Import TFJ", paiement: "Paiement", encaissement: "Encaissement", cloture: "Clôture", reouverture: "Réouverture",
  deconnexion: "Déconnexion", echec_connexion: "Échec de connexion", changement_mot_de_passe: "Changement de mot de passe",
  creation_role: "Création de rôle", modification_role: "Modification des permissions", suppression_role: "Suppression de rôle",
};
export const actionLabel = (a) => ACTION_LABELS[a] || a;

function displayValue(f, item) {
  const v = item[f.name];
  if (f.type === "ref" || f.type === "refitem" || f.type === "multirefitem") return item[`${f.name}_label`] || "—";
  if (f.type === "select") return f.options.find((o) => o.value === v)?.label || "—";
  if (f.type === "number") return v == null ? "—" : f.money ? fmtMoney(v) : fmtNum(v, 2).replace(/,00$/, "");
  if (f.type === "date") return fmtDate(v);
  if (f.type === "bool") return v ? "Oui" : "Non";
  return v || "—";
}
export { displayValue };

export function DetailSheet({ open, onOpenChange, item, cfg, res, extra }) {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    if (!open || !item) return;
    api.get(`/audit?entity_id=${item.id}&module=${cfg.module}&size=200`).then((r) => setLogs(r.data.items)).catch(() => setLogs([]));
  }, [open, item, cfg.module]);
  if (!item) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto" data-testid="detail-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-3">
            {item.numero || item.code || cfg.singular}
            {item.statut && <StatusBadge value={item.statut} />}
          </SheetTitle>
        </SheetHeader>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mt-6 text-sm">
          {cfg.fields.map((f) => (
            <div key={f.name} className={f.type === "textarea" ? "col-span-2" : ""}>
              <dt className="text-[10px] uppercase tracking-wider text-muted-foreground">{f.label}</dt>
              <dd className={`mt-0.5 ${f.type === "number" ? "font-mono" : ""}`}>{displayValue(f, item)}</dd>
            </div>
          ))}
          {item.motif_annulation && (
            <div className="col-span-2 p-2 rounded bg-red-50 border border-red-200 text-red-800 text-xs">
              Annulé par {item.cancelled_by_name} le {fmtDate(item.cancelled_at)} — {item.motif_annulation}
            </div>
          )}
          {item.corrige_de_numero && (
            <div className="col-span-2 text-xs text-muted-foreground">Correction de {item.corrige_de_numero}</div>
          )}
          <div className="col-span-2 text-[11px] text-muted-foreground border-t pt-3">
            Créé par {item.created_by_name || "système"} le {fmtDate(item.created_at)}
            {item.validated_by_name && ` · Validé par ${item.validated_by_name} le ${fmtDate(item.validated_at)}`}
          </div>
        </dl>
        {extra}
        <h4 className="mt-8 mb-3 text-xs font-bold uppercase tracking-wider">Historique des opérations</h4>
        <ol className="relative border-l border-border ml-2 space-y-4" data-testid="detail-history">
          {logs.map((l) => (
            <li key={l.id} className="ml-4">
              <span className="absolute -left-1.5 mt-1.5 h-3 w-3 rounded-full bg-primary border-2 border-background" />
              <div className="text-sm font-semibold">{actionLabel(l.action)}</div>
              <div className="text-[11px] text-muted-foreground">{fmtDate(l.date)} · {l.user_name}</div>
              {l.motif && <div className="text-xs mt-1">Motif : {l.motif}</div>}
            </li>
          ))}
          {!logs.length && <li className="ml-4 text-xs text-muted-foreground">Aucun historique</li>}
        </ol>
      </SheetContent>
    </Sheet>
  );
}
